import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import HeroSection from '../components/sections/HeroSection';
import StatsSection from '../components/sections/StatsSection';
import ContentSection from '../components/sections/ContentSection';
import TiltedFeatureSection from '../components/sections/TiltedFeatureSection';

const HomePage: React.FC = () => {
  // Stats data from the PDF
  const statsData = [
    { value: '200M', label: 'Active Websites' },
    { value: '30M', label: 'E-commerce Websites' },
    { value: '250K', label: 'New Websites Daily' }
  ];

  // Features data from the PDF
  const featuresData = [
    {
      title: 'Comprehensive Knowledge',
      description: 'AiDA has extensive knowledge about your business and services, ensuring accurate and helpful responses to customer inquiries.'
    },
    {
      title: 'Quick Information Retrieval',
      description: 'Find accurate information instantly, eliminating customer frustration with difficult-to-navigate websites and knowledge bases.'
    },
    {
      title: 'Multi-Role Functionality',
      description: 'AiDA plays multiple roles from customer assistance to sales representative and brand ambassador, providing consistent service across touchpoints.'
    },
    {
      title: 'Always Available',
      description: 'Available 24/7, AiDA ensures your customers receive assistance whenever they need it, without waiting or scheduling constraints.'
    },
    {
      title: 'Multilingual Support',
      description: 'Communicate with customers in multiple languages, expanding your reach and improving accessibility for diverse audiences.'
    },
    {
      title: 'Cost-Effective Solution',
      description: 'Reduce operational costs while maintaining high-quality customer service, allowing you to allocate resources more efficiently.'
    }
  ];

  return (
    <>
      <HeroSection 
        title="Introducing AiDA: AI Empowered Digital Assistant" 
        subtitle="A next-generation low-cost advanced LLM based assistant for online businesses and organizations to deliver human-like, personalized, efficient, and secure customer interaction."
        ctaText="Learn More"
        ctaLink="/products"
      />
      
      <StatsSection stats={statsData} />
      
      <ContentSection 
        title="Transforming Customer Experience"
        content="In today's rapidly evolving digital landscape, online businesses are growing at an unprecedented rate. This explosive growth creates both opportunities and challenges for businesses seeking to provide exceptional customer service at scale.\n\nAiDA transforms how businesses interact with their customers online. Unlike traditional customer service solutions that often leave users frustrated with difficult-to-find information, complex query handling, slow response times, and inconsistent service quality, AiDA offers a revolutionary approach to customer engagement.\n\nOur AI-powered digital assistant provides comprehensive knowledge about your business and services, quickly finds accurate information, plays multiple roles from customer assistance to sales representative, and delivers this with genuine empathy. AiDA is always available 24/7, supports multiple languages, reduces operational costs, requires minimal training time, and maintains consistent quality standards across all customer interactions."
        imageUrl="/assets/images/aida-home.png"
      />
      
      <TiltedFeatureSection 
        title="Key Capabilities" 
        features={featuresData} 
      />
      
      <div className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Customer Experience?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contact us today to schedule a demonstration and discover how our AI Digital Assistant can revolutionize your business operations while enhancing customer satisfaction.
          </p>
          <Button asChild variant="accent" size="lg">
            <Link to="/contact">
              Get Started
            </Link>
          </Button>
        </div>
      </div>
    </>
  );
};

export default HomePage;
